import { useState, useEffect } from "react";
import { T } from "../../config/theme.js";
import { api } from "../../hooks/useApi.js";
import { Btn } from "../ui/index.jsx";
import { ChevronLeft, ChevronRight, Shield, AlertTriangle, TrendingDown, Eye } from "lucide-react";

// ═══════════════════════════════════════════
// 월간 효용 리포트 (Sprint 17 재설계)
// 행동 복기 중심 · 5블록 · 1열 스크롤
// ═══════════════════════════════════════════

export default function MonthlyReport({ token, onBack }) {
  const [actions, setActions] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [reports, setReports] = useState([]);
  const [telemetry, setTelemetry] = useState([]);
  const [prevMonthData, setPrevMonthData] = useState(null);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [showTransactions, setShowTransactions] = useState(false);

  useEffect(() => {
    api("/actions", {}, token).then(d => setActions(d || [])).catch(() => {});
    api("/holdings", {}, token).then(d => setHoldings(d || [])).catch(() => {});
    api("/reports?limit=200", {}, token).then(d => setReports(Array.isArray(d) ? d : [])).catch(() => {});
    // 텔레메트리 (stock.view 집계용)
    api("/telemetry?event=stock.view&limit=500", {}, token).then(d => setTelemetry(d || [])).catch(() => setTelemetry([]));
  }, []);

  // ── 월 네비게이션 ──
  const changeMonth = (dir) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  const monthLabel = (() => {
    const [y, m] = month.split("-");
    return `${y}년 ${parseInt(m)}월`;
  })();
  const prevMonth = (() => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 2, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  })();

  // ── 데이터 필터링 ──
  const monthActions = actions.filter(a => a.created_at?.startsWith(month));
  const prevActions = actions.filter(a => a.created_at?.startsWith(prevMonth));
  const monthReports = reports.filter(r => r.snapshot_at?.startsWith(month));
  const monthTelemetry = telemetry.filter(t => t.created_at?.startsWith(month));
  const prevTelemetry = telemetry.filter(t => t.created_at?.startsWith(prevMonth));

  // active_hold 기록
  const activeHolds = monthActions.filter(a => a.action_type === "active_hold");
  const prevActiveHolds = prevActions.filter(a => a.action_type === "active_hold");

  // 거래 기록 (active_hold 제외)
  const trades = monthActions.filter(a => a.action_type !== "active_hold");
  const prevTrades = prevActions.filter(a => a.action_type !== "active_hold");

  // 상태 변경
  const stateChanges = monthReports.filter(r => r.payload?.state_changed);

  // 반복 확인 (stock.view 일별 집계)
  const recheckCount = monthTelemetry.length;
  const prevRecheckCount = prevTelemetry.length;

  // 경고 무시 행동 (CAUTION/PAUSE 상태에서 매수)
  const warningIgnores = monthActions.filter(a =>
    (a.phase_state === "CAUTION" || a.phase_state === "PAUSE") &&
    (a.action_type === "add_buy")
  );
  const prevWarningIgnores = prevActions.filter(a =>
    (a.phase_state === "CAUTION" || a.phase_state === "PAUSE") &&
    (a.action_type === "add_buy")
  );

  // ══════════════════════════════════════════
  // 블록 1: 한 줄 요약 생성
  // ══════════════════════════════════════════
  const generateSummary = () => {
    if (monthActions.length === 0 && activeHolds.length === 0) {
      return "큰 행동 없이 지나간 달이었습니다. 불필요한 반응이 적었다는 점을 함께 확인할 수 있습니다.";
    }
    if (activeHolds.length >= 3) {
      return "경고 구간에서의 관망 유지가 여러 차례 있었던 달이었습니다. 기준 유지가 행동보다 많았습니다.";
    }
    if (activeHolds.length > prevActiveHolds.length) {
      return "이번 달은 경고 구간에서의 과잉 반응이 지난달보다 줄었습니다.";
    }
    if (trades.length === 0) {
      return "변화가 적었던 달이었습니다. 기준을 유지한 것 자체가 의미 있었습니다.";
    }
    if (warningIgnores.length === 0 && trades.length > 0) {
      return "급한 판단보다 관찰과 유지가 더 많았던 흐름이었습니다.";
    }
    return "이번 달은 큰 행동보다 기준 유지가 더 많았던 달이었습니다.";
  };

  // ══════════════════════════════════════════
  // 블록 2: 가장 잘한 판단 1건
  // ══════════════════════════════════════════
  const findBestDecision = () => {
    // 1순위: active_hold 후 이후 하락 확인
    for (const hold of activeHolds) {
      const laterReports = monthReports.filter(r =>
        r.symbol === hold.symbol && r.snapshot_at > hold.created_at
      );
      const declined = laterReports.some(r => r.micro?.pnl_1d < 0 || r.payload?.risk_delta > 0);
      if (declined) {
        return {
          date: hold.created_at?.slice(0, 10),
          name: hold.name || hold.symbol,
          badge: "관망 유지",
          action: `${hold.name || hold.symbol}이(가) ${hold.phase_state === "CAUTION" ? "주의" : "보류"} 상태일 때 추가 행동을 하지 않았습니다.`,
          result: "이후 단기 하락 구간이 이어졌습니다. 관망이 결과적으로 더 보수적인 선택이었습니다.",
        };
      }
    }

    // 2순위: active_hold 기록이 있으면 (결과 불문)
    if (activeHolds.length > 0) {
      const hold = activeHolds[0];
      return {
        date: hold.created_at?.slice(0, 10),
        name: hold.name || hold.symbol,
        badge: "기준 유지",
        action: `${hold.name || hold.symbol}에서 즉시 반응하지 않고 관망을 유지했습니다.`,
        result: "즉시 반응보다 기준 유지가 더 안정적이었던 흐름이었습니다.",
      };
    }

    // 3순위: 거래 없는 안정적인 달
    if (trades.length === 0 && holdings.length > 0) {
      return {
        date: null,
        name: null,
        badge: "추가 행동 없음",
        action: "이번 달은 별도의 거래 없이 기존 포지션을 유지했습니다.",
        result: "불필요한 거래를 줄인 것 자체가 기준을 지킨 행동입니다.",
      };
    }

    return null;
  };

  // ══════════════════════════════════════════
  // 블록 3: 가장 위험했던 순간 1건
  // ══════════════════════════════════════════
  const findRiskiestMoment = () => {
    // 1순위: 경고 무시 후 매수
    if (warningIgnores.length > 0) {
      const w = warningIgnores[0];
      return {
        date: w.created_at?.slice(0, 10),
        name: w.name || w.symbol,
        badge: "주의 구간 반응",
        action: `${w.phase_state === "CAUTION" ? "주의" : "보류"} 구간에서 추가 매수가 발생했습니다.`,
        result: "경고 구간에서의 추가 반응이 결과적으로 더 불리한 흐름이었을 수 있습니다.",
      };
    }

    // 2순위: 조기 매도 (WATCH 상태에서 매도)
    const earlySells = monthActions.filter(a =>
      a.phase_state === "WATCH" && a.action_type === "partial_sell"
    );
    if (earlySells.length > 0) {
      const s = earlySells[0];
      return {
        date: s.created_at?.slice(0, 10),
        name: s.name || s.symbol,
        badge: "조기 정리",
        action: `관찰 구간에서 일부 매도가 발생했습니다.`,
        result: "이후 반등 구간이 이어졌을 가능성이 있습니다. 경과 확인이 더 적절했을 수 있습니다.",
      };
    }

    // 3순위: 반복 확인 많은 날
    if (recheckCount > 30) {
      return {
        date: null,
        name: null,
        badge: "초기 반응",
        action: "이번 달 종목 확인 빈도가 높았습니다.",
        result: "변화보다 불안 반응이 앞섰던 구간이 있었을 수 있습니다.",
      };
    }

    return null;
  };

  const bestDecision = findBestDecision();
  const riskiestMoment = findRiskiestMoment();

  // ══════════════════════════════════════════
  // 블록 4: 행동 안정도 데이터
  // ══════════════════════════════════════════
  const stabilityMetrics = [
    {
      label: "관망 유지",
      icon: Shield,
      prev: prevActiveHolds.length,
      curr: activeHolds.length,
      unit: "회",
      interpret: activeHolds.length > prevActiveHolds.length
        ? "경고 구간에서 관망을 유지한 기록이 늘었습니다."
        : activeHolds.length === prevActiveHolds.length
        ? "지난달과 비슷한 수준이었습니다."
        : "지난달보다 관망 유지 기록이 줄었습니다.",
    },
    {
      label: "반복 확인",
      icon: Eye,
      prev: prevRecheckCount,
      curr: recheckCount,
      unit: "회",
      interpret: recheckCount < prevRecheckCount
        ? "반복 확인이 지난달보다 줄었습니다."
        : recheckCount === prevRecheckCount
        ? "지난달과 비슷한 수준이었습니다."
        : "반복 확인이 지난달보다 늘었습니다.",
    },
    {
      label: "경고 무시 행동",
      icon: AlertTriangle,
      prev: prevWarningIgnores.length,
      curr: warningIgnores.length,
      unit: "회",
      interpret: warningIgnores.length < prevWarningIgnores.length
        ? "경고 직후의 신규 행동이 줄었습니다."
        : warningIgnores.length === 0
        ? "경고 구간에서의 무시 행동이 없었습니다."
        : "경고 구간에서 신규 행동이 발생했습니다.",
    },
  ].filter(m => m.prev > 0 || m.curr > 0);

  // ══════════════════════════════════════════
  // 블록 5: 패턴 감지
  // ══════════════════════════════════════════
  const generatePattern = () => {
    if (earlySellPattern()) {
      return "관찰(WATCH) 초기 구간에서 즉시 반응이 반복되는 경향이 있었습니다. 다음 달에는 경과 확인이 더 중요할 수 있습니다.";
    }
    if (recheckCount > prevRecheckCount * 1.5 && prevRecheckCount > 0) {
      return "하락장보다 횡보 구간에서 반복 확인이 많았습니다. 다음 달에는 변화 없는 날의 확인 빈도를 함께 보시는 것이 좋습니다.";
    }
    if (warningIgnores.length >= 2) {
      return "경고 구간에서의 추가 행동이 반복되었습니다. 다음 달에는 경고 신호 확인 후 판단 유예가 더 적절할 수 있습니다.";
    }
    return null;
  };

  function earlySellPattern() {
    const watchSells = monthActions.filter(a =>
      a.phase_state === "WATCH" && a.action_type === "partial_sell"
    );
    return watchSells.length >= 2;
  }

  const patternText = generatePattern();

  // ══════════════════════════════════════════
  // 렌더링
  // ══════════════════════════════════════════
  return (
    <div>
      <Btn variant="ghost" onClick={onBack} style={{ marginBottom: 16 }}>
        <ChevronLeft size={16} /> 돌아가기
      </Btn>

      {/* 월 네비게이션 */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0, color: T.text }}>월간 행동 리포트</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => changeMonth(-1)} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, padding: "4px 10px", cursor: "pointer" }}>
            <ChevronLeft size={14} />
          </button>
          <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{monthLabel}</span>
          <button onClick={() => changeMonth(1)} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, padding: "4px 10px", cursor: "pointer" }}>
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* 5블록 컨테이너 — gap 통제 */}
      <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>

        {/* ═══ 블록 1: 한 줄 요약 ═══ */}
        <div>
          <div style={{ fontSize: 18, fontWeight: 600, color: T.text, lineHeight: 1.6 }}>
            {generateSummary()}
          </div>
        </div>

        {/* ═══ 블록 2: 가장 잘한 판단 ═══ */}
        {bestDecision && (
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: "24px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.textDim, marginBottom: 16 }}>이번 달 가장 잘한 판단</div>
            {bestDecision.date && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <span className="mono-num" style={{ fontSize: 12, color: T.textMuted }}>{bestDecision.date}</span>
                {bestDecision.name && <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{bestDecision.name}</span>}
                <span style={{
                  fontSize: 11, padding: "3px 10px", borderRadius: 6, fontWeight: 600,
                  background: T.accentSoft, color: T.accentSoftText,
                }}>{bestDecision.badge}</span>
              </div>
            )}
            {!bestDecision.date && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <span style={{
                  fontSize: 11, padding: "3px 10px", borderRadius: 6, fontWeight: 600,
                  background: T.accentSoft, color: T.accentSoftText,
                }}>{bestDecision.badge}</span>
              </div>
            )}
            <div style={{ fontSize: 14, color: T.text, lineHeight: 1.7, marginBottom: 10 }}>
              {bestDecision.action}
            </div>
            <div style={{ fontSize: 13, color: T.textDim, lineHeight: 1.6 }}>
              {bestDecision.result}
            </div>
          </div>
        )}

        {/* ═══ 블록 3: 가장 위험했던 순간 ═══ */}
        {riskiestMoment && (
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: "24px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.textDim, marginBottom: 16 }}>이번 달 가장 위험했던 순간</div>
            {riskiestMoment.date && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <span className="mono-num" style={{ fontSize: 12, color: T.textMuted }}>{riskiestMoment.date}</span>
                {riskiestMoment.name && <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{riskiestMoment.name}</span>}
                <span style={{
                  fontSize: 11, padding: "3px 10px", borderRadius: 6, fontWeight: 600,
                  background: "rgba(255,255,255,0.06)", color: T.textDim,
                }}>{riskiestMoment.badge}</span>
              </div>
            )}
            {!riskiestMoment.date && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <span style={{
                  fontSize: 11, padding: "3px 10px", borderRadius: 6, fontWeight: 600,
                  background: "rgba(255,255,255,0.06)", color: T.textDim,
                }}>{riskiestMoment.badge}</span>
              </div>
            )}
            <div style={{ fontSize: 14, color: T.text, lineHeight: 1.7, marginBottom: 10 }}>
              {riskiestMoment.action}
            </div>
            <div style={{ fontSize: 13, color: T.textDim, lineHeight: 1.6 }}>
              {riskiestMoment.result}
            </div>
          </div>
        )}

        {/* ═══ 블록 4: 행동 안정도 변화 ═══ */}
        {stabilityMetrics.length > 0 && (
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: "24px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.textDim, marginBottom: 16 }}>행동 안정도 변화</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {stabilityMetrics.map((m, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <m.icon size={14} color={T.textMuted} strokeWidth={1.5} />
                      <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{m.label}</span>
                    </div>
                    <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.5 }}>{m.interpret}</div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div className="mono-num" style={{ fontSize: 13, color: T.textMuted }}>
                      {m.prev}{m.unit} → <span style={{ color: T.text, fontWeight: 700 }}>{m.curr}{m.unit}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ 블록 5: 다음 달 주의할 패턴 ═══ */}
        <div style={{
          border: patternText ? `1px solid ${T.border}` : `1px dashed rgba(255,255,255,0.1)`,
          borderRadius: 14, padding: "24px",
          background: patternText ? T.card : "transparent",
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.textDim, marginBottom: 12 }}>다음 달 주의할 패턴</div>
          <div style={{ fontSize: 14, color: patternText ? T.text : T.textMuted, lineHeight: 1.7 }}>
            {patternText || "특별한 주의 패턴은 발견되지 않았습니다. 현재 기준을 유지해도 좋습니다."}
          </div>
        </div>

        {/* ═══ 하단: 거래 내역 (접힘) ═══ */}
        {trades.length > 0 && (
          <div>
            <button onClick={() => setShowTransactions(!showTransactions)} style={{
              background: "none", border: "none", color: T.textMuted, cursor: "pointer",
              fontSize: 13, fontWeight: 600, padding: 0, fontFamily: "inherit",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              {showTransactions ? "거래 내역 접기" : `이번 달 거래 내역 ${trades.length}건 보기`}
              <ChevronRight size={14} style={{ transform: showTransactions ? "rotate(90deg)" : "none", transition: "transform 0.2s" }} />
            </button>

            {showTransactions && (
              <div style={{ marginTop: 12, background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: "16px 20px" }}>
                {trades.map((a, i) => (
                  <div key={i} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "10px 0",
                    borderBottom: i < trades.length - 1 ? `1px solid ${T.border}` : "none",
                  }}>
                    <div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{a.name || a.symbol}</span>
                      <span className="mono-num" style={{ fontSize: 11, color: T.textMuted, marginLeft: 8 }}>
                        {{ add_buy: "추가매수", partial_sell: "일부매도", full_sell: "전량매도" }[a.action_type] || a.action_type}
                      </span>
                    </div>
                    <span className="mono-num" style={{ fontSize: 11, color: T.textMuted }}>
                      {a.created_at ? new Date(a.created_at).toLocaleDateString("ko-KR") : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
